import { FileButton, Loader, Menu, Tooltip, UnstyledButton } from "@mantine/core";
import { LibraryBig, Upload, X } from "lucide-react";
import { useRef, type ReactNode } from "react";

import type { PickedImage } from "../../../platform/media/ImagePicker";
import { VideoFrame } from "../../../ui/VideoFrame";
import { UNSUPPORTED } from "../../../platform/media/ImageRefsSlot";
import classes from "../../../platform/media/slots.module.css";

export interface MediaRefs {
  /** The chosen videos or audios, each with its poster (none for an audio) and name. */
  items: PickedImage[];
  /** Live's `@Video1` or `@Audio1` pill on each, where the mode tags its medium. */
  tagged: boolean;
  /** The first pill's number: 1, or after a tool's own video, which is `@Video1` (Kling 4.0 Edit). */
  firstTag?: number;
  /** The inputs chosen leave no mode that takes it: live greys the tile. */
  disabled: boolean;
  /** No more fit: the tile greys out, with no message (live). */
  full: boolean;
  /** Live's slot tooltip: the mode's media limits (none where it has none). */
  limits: string[];
  onFromLibrary: () => void;
  onUpload: (file: File | null) => void;
  uploading: boolean;
  onRemove: (assetId: string) => void;
}

export interface MediaRefsSlotProps {
  /** Live's tile words: "Video ref", "Audio refs". */
  label: string;
  /** The tag's word: "Video", "Audio". */
  tag: string;
  icon: ReactNode;
  /** The files an upload takes. */
  accept: readonly string[];
  refs: MediaRefs;
}

/**
 * A reference video's videos or audios (#260, capture 03, `actions-raw.md` "Multi-Input slot
 * pickers"): the chosen ones before the tile, each with live's x and, where the mode tags its
 * medium, its pill; the tile opens live's menu (From library, Upload), with the mode's limits as
 * its tooltip, greyed with live's words where the inputs chosen leave no mode that takes it.
 */
export function MediaRefsSlot({ label, tag, icon, accept, refs }: MediaRefsSlotProps) {
  const resetChooser = useRef<() => void>(null);
  const tile = (
    <UnstyledButton
      className={classes.slot}
      aria-label={label}
      aria-busy={refs.uploading}
      disabled={refs.disabled || refs.full}
    >
      {refs.uploading ? <Loader size={20} aria-hidden /> : icon}
      <span className={classes.slotLabel}>{label}</span>
    </UnstyledButton>
  );
  const tip = refs.disabled ? [UNSUPPORTED] : refs.limits;
  return (
    <>
      {refs.items.map((item, at) => (
        <span key={item.assetId} className={classes.slotFilled}>
          <span className={classes.slotChosen}>
            {item.url === null && tag === "Video" && item.original ? (
              // A video with no still yet (no poster): its first frame, muted, never played.
              <VideoFrame
                src={item.original}
                label={item.name}
                still
                fallback={<span className={classes.mediaName}>{item.name}</span>}
              />
            ) : item.url === null ? (
              <span className={classes.mediaName}>{item.name}</span>
            ) : (
              <img src={item.url} alt={item.name} className={classes.slotPicture} />
            )}
            {refs.tagged ? (
              <span className={classes.tag}>
                @{tag}
                {(refs.firstTag ?? 1) + at}
              </span>
            ) : null}
          </span>
          <UnstyledButton
            className={classes.thumbRemove}
            aria-label={`Remove ${item.name}`}
            onClick={() => refs.onRemove(item.assetId)}
          >
            <X size={14} strokeWidth={2.5} aria-hidden />
          </UnstyledButton>
        </span>
      ))}
      <Tooltip
        label={tip.map((line) => (
          <div key={line}>{line}</div>
        ))}
        disabled={tip.length === 0}
        multiline
        withArrow
      >
        <span className={classes.slotFilled}>
          {refs.disabled ? (
            tile
          ) : (
            <FileButton
              accept={accept.join(",")}
              resetRef={resetChooser}
              onChange={(file) => {
                refs.onUpload(file);
                resetChooser.current?.();
              }}
              inputProps={{ "aria-label": `Choose a ${tag.toLowerCase()} reference to upload` }}
            >
              {(chooser) => (
                <Menu position="top-start" disabled={refs.full}>
                  <Menu.Target>{tile}</Menu.Target>
                  <Menu.Dropdown className={classes.frameMenu}>
                    <Menu.Item leftSection={<LibraryBig size={16} />} onClick={refs.onFromLibrary}>
                      From library
                    </Menu.Item>
                    <Menu.Item leftSection={<Upload size={16} />} onClick={chooser.onClick}>
                      Upload
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </FileButton>
          )}
        </span>
      </Tooltip>
    </>
  );
}
